import React, { useEffect, useRef, useState } from "react";
import {
  mdiCurtains,
  mdiCurtainsClosed,
  mdiGate,
  mdiGateOpen,
  mdiPause,
  mdiWindowShutter,
  mdiWindowShutterOpen,
} from "@mdi/js";
import { IoSunny, IoSunnyOutline } from "react-icons/io5";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { DEVICE_TYPES as T, MODELS, TRAITS, isModel, typeOf } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { useToggleTop } from "../../../components/common/ToggleTop";

const action = (device, trait, id, value) => ({
  on: [`${device.applianceId}`],
  off: [],
  action: { [device.applianceId]: { id, trait: Number(trait), value } },
});

// Shows a short status after a command, like the app's "Opening..." text.
const useStatus = () => {
  const [status, setStatus] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (text) => {
    clearTimeout(timer.current);
    setStatus(text);
    timer.current = setTimeout(() => setStatus(""), 5000);
  };
  return [status, show];
};

// ---------- Fan speed (app/components/Regulator.js) ----------

// Six speed buttons around a knob: 1 at the bottom, going clockwise.
export const FanRegulator = ({ speed, onSpeed, disabled }) => {
  const current = Number(speed) || 3;
  return (
    <div className="hx-regulator" role="group" aria-label="Fan speed">
      <div className="hx-regulator-knob" style={{ transform: `rotate(${(current - 1) * 60}deg)` }} aria-hidden="true" />
      {[1, 2, 3, 4, 5, 6].map((value) => {
        const angle = ((value - 1) * 60 * Math.PI) / 180;
        const left = 50 - 44 * Math.sin(angle);
        const top = 50 + 44 * Math.cos(angle);
        return (
          <button
            key={value}
            type="button"
            className={`hx-regulator-speed ${value === current ? "is-selected" : ""}`}
            style={{ left: `${left}%`, top: `${top}%` }}
            onClick={() => onSpeed(value)}
            disabled={disabled}
            aria-label={`Speed ${value}`}
            aria-pressed={value === current}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
};

// ---------- Dimmer (trait 1003) ----------

const PRESETS = [0, 25, 50, 75, 100];

export const DimmerControl = ({ value, onCommit, disabled }) => {
  const [level, setLevel] = useState(Number(value) || 0);
  useEffect(() => setLevel(Number(value) || 0), [value]);
  const commit = (next) => {
    setLevel(next);
    onCommit(next);
  };
  return (
    <div className="hx-dimmer">
      <div className="hx-dimmer-head">
        <span id="hx-dimmer-label">Brightness</span>
        <span>{level} %</span>
      </div>
      <div className="hx-dimmer-slider">
        <IoSunnyOutline aria-hidden="true" />
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={level}
          disabled={disabled}
          aria-labelledby="hx-dimmer-label"
          onChange={(event) => setLevel(Number(event.target.value))}
          onPointerUp={(event) => commit(Number(event.currentTarget.value))}
          onKeyUp={(event) => commit(Number(event.currentTarget.value))}
        />
        <IoSunny aria-hidden="true" />
      </div>
      <div className="hx-dimmer-presets">
        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className={preset === level ? "is-selected" : ""}
            onClick={() => commit(preset)}
            disabled={disabled}
          >
            {preset} %
          </button>
        ))}
      </div>
    </div>
  );
};

// ---------- Segmented Open / Stop / Close control ----------

const Segmented = ({ label, icons, captions, onPress, border }) => (
  <div className="hx-seg" style={border ? { "--hx-seg-border": border } : undefined}>
    {label ? <p className="hx-seg-label">{label}</p> : null}
    <div className="hx-seg-pill" role="group" aria-label={label ?? "Controls"}>
      {icons.map((icon, index) => (
        <button key={captions[index]} type="button" onClick={() => onPress(index)} aria-label={`${label ? `${label} ` : ""}${captions[index]}`}>
          <MdiIcon path={icon} size={32} />
        </button>
      ))}
    </div>
    <div className="hx-seg-captions" aria-hidden="true">
      {captions.map((caption) => (
        <span key={caption}>{caption}</span>
      ))}
    </div>
  </div>
);

// ---------- Curtains (trait 901) ----------

// Relay ids per motor, as the dashboard has always sent them (the app's
// schedules use the same scheme): open, stop, close.
const CURTAIN_COMMANDS = {
  1: [
    [1, 1],
    [1, 0],
    [2, 1],
  ],
  2: [
    [3, 1],
    [4, 0],
    [4, 1],
  ],
};
const CURTAIN_WORDS = ["Opening...", "Stopped", "Closing..."];

export const CurtainControl = ({ device }) => {
  const toast = useToggleTop();
  const [status, showStatus] = useStatus();
  const motors = isModel(device, MODELS.W2CM_1) ? [1, 2] : [1];

  const press = async (motor, index) => {
    const [id, value] = CURTAIN_COMMANDS[motor][index];
    try {
      await sendControl(action(device, TRAITS.CURTAIN, id, value));
      showStatus(motors.length > 1 ? `Motor ${motor}: ${CURTAIN_WORDS[index]}` : CURTAIN_WORDS[index]);
    } catch (err) {
      toast("Couldn't reach the curtain. Please try again.", "red");
    }
  };

  return (
    <>
      {motors.map((motor) => (
        <Segmented
          key={motor}
          label={motors.length > 1 ? `Motor ${motor}` : null}
          icons={[mdiCurtains, mdiPause, mdiCurtainsClosed]}
          captions={["Open", "Stop", "Close"]}
          border="#4B5234"
          onPress={(index) => press(motor, index)}
        />
      ))}
      {status ? (
        <p className="hx-status-pill" role="status">
          {status}
        </p>
      ) : null}
    </>
  );
};

// ---------- Gates ----------

const GATE_WORDS = ["Opening", "Stopping", "Closing"];
const GATE_DONE = { gate: ["Gate Opened!", "Gate Stopped!", "Gate Closed!"], shutter: ["Shutter Up!", "Shutter Stopped!", "Shutter Down!"] };

// GC_1 takes the legacy gc command; WGC_1/WGC_2 take trait 601
// (app/screens/CommonSwichDetails.js gate_updateState and WGC_State).
const gatePayload = (device, index, leaf) => {
  const aid = device.applianceId;
  const swing = typeOf(device) === T.SWING_GATE;
  if (isModel(device, MODELS.GC_1)) {
    // index: 0 open, 1 stop, 2 close -> action 0 open / 1 close / 2 stop, rl 1 / 2 / 3.
    const step = [0, 2, 1][index];
    return { on: [`${aid}`], off: [], gc: { [aid]: { action: step, rl: swing ? Number(device.pin) : step + 1 } } };
  }
  const value = [1, 3, 2][index];
  if (swing) return action(device, TRAITS.GATE, leaf, value);
  return action(device, TRAITS.GATE, value, 1);
};

export const GateControl = ({ device }) => {
  const toast = useToggleTop();
  const [status, showStatus] = useStatus();
  const shutter = typeOf(device) === T.SHUTTER_GATE;
  const swing = typeOf(device) === T.SWING_GATE;
  const captions = shutter ? ["Up", "Stop", "Down"] : ["Open", "Stop", "Close"];
  const icons = shutter ? [mdiWindowShutterOpen, mdiPause, mdiWindowShutter] : [mdiGateOpen, mdiPause, mdiGate];

  const press = async (index, leaf = 1) => {
    showStatus(GATE_WORDS[index]);
    try {
      await sendControl(gatePayload(device, index, leaf));
      setTimeout(() => showStatus(GATE_DONE[shutter ? "shutter" : "gate"][index]), 1200);
    } catch (err) {
      showStatus("");
      toast("Couldn't reach the gate. Please try again.", "red");
    }
  };

  if (isModel(device, MODELS.GC_1)) {
    return (
      <>
        <div className="hx-gate-buttons" role="group" aria-label="Gate controls">
          {captions.map((caption, index) => (
            <button
              key={caption}
              type="button"
              className={`hx-gate-button ${index === 1 ? "is-stop" : ""}`}
              onClick={() => press(index)}
            >
              <span aria-hidden="true">
                <MdiIcon path={icons[index]} size={40} />
              </span>
              <span>{caption}</span>
            </button>
          ))}
        </div>
        {status ? <p className="hx-status-pill" role="status">{status}</p> : null}
      </>
    );
  }

  // WGC_2 drives one leaf per appliance (chosen by pin); WGC_1 drives both.
  const leaves = swing ? (isModel(device, MODELS.WGC_2) ? [Number(device.pin) || 1] : [1, 2]) : [null];
  return (
    <>
      {leaves.map((leaf) => (
        <Segmented
          key={leaf ?? "gate"}
          label={leaf ? (leaf === 1 ? "Left Gate" : "Right Gate") : null}
          icons={icons}
          captions={captions}
          border="#B2AA95"
          onPress={(index) => press(index, leaf ?? 1)}
        />
      ))}
      {status ? <p className="hx-status-pill" role="status">{status}</p> : null}
    </>
  );
};
